import React, {useEffect, useState} from "react";
import {motion} from "framer-motion";
import {FaLinkedin, FaGithub} from "react-icons/fa";
import {Link} from "react-scroll";
import TechStack from "./TechStack";

const ROLES = ["L1 Support Associate Team Leader", "Backend Developer", "Network & Systems Support"];

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function useTypeCycle(words, holdMs = 2600) {
  const [text, setText] = useState(words[0]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    let idx = 0;
    let cancelled = false;

    function scrambleTo(target) {
      return new Promise((resolve) => {
        let frame = 0;
        const totalFrames = 16;
        const timer = setInterval(() => {
          if (cancelled) {
            clearInterval(timer);
            return resolve();
          }
          const reveal = Math.floor((frame / totalFrames) * target.length);
          let out = "";
          for (let i = 0; i < target.length; i++) {
            out += i < reveal || target[i] === " " ? target[i] : CHARS[Math.floor(Math.random() * CHARS.length)];
          }
          setText(out);
          frame++;
          if (frame > totalFrames) {
            clearInterval(timer);
            setText(target);
            resolve();
          }
        }, 40);
      });
    }

    async function loop() {
      while (!cancelled) {
        await new Promise((r) => setTimeout(r, holdMs));
        idx = (idx + 1) % words.length;
        await scrambleTo(words[idx]);
      }
    }
    loop();

    return () => {
      cancelled = true;
    };
  }, [words, holdMs]);

  return text;
}

export default function Body() {
  const role = useTypeCycle(ROLES);

  return (
    <div id="home" className="bg-ink text-white pt-36 pb-16 md:pt-44">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
        <motion.div initial={{opacity: 0, y: 16}} animate={{opacity: 1, y: 0}} transition={{duration: 0.6}}>
          <div className="flex items-center gap-2.5 font-mono text-xs tracking-widest text-amber mb-5">
            <span className="w-5 h-px bg-amber inline-block" />
            GROUND CONTROL — POKHARA, NP
          </div>

          <h1 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-4">
            Dinesh Bahadur Khawas
          </h1>

          <div className="font-mono text-mint text-sm sm:text-base mb-6 min-h-[24px]">
            {role}
            <span className="inline-block w-2 h-4 bg-mint ml-1 align-middle blink-cursor" />
          </div>

          <p className="text-muted text-base max-w-md mb-8">
            IT graduate keeping critical airport systems, backend services and campus networks running. Systematic under
            pressure, and always learning the next stack.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="contact"
              smooth={true}
              duration={500}
              offset={-64}
              className="font-mono text-sm tracking-wide border border-amber text-amber px-6 py-3 cursor-pointer hover:bg-amber hover:text-ink transition-colors">
              Get in touch
            </Link>
            <Link
              to="experience"
              smooth={true}
              duration={500}
              offset={-64}
              className="font-mono text-sm tracking-wide border border-line text-white px-6 py-3 cursor-pointer hover:bg-panel2 transition-colors">
              View experience
            </Link>
            <div className="flex items-center gap-4 ml-1">
              <a
                href="https://github.com/Dinesh-Thakuri"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="text-muted hover:text-white transition-colors">
                <FaGithub size={22} />
              </a>
              <a
                href="https://https://www.linkedin.com/in/dinesh-khawas-062aa021b/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-muted hover:text-white transition-colors">
                <FaLinkedin size={22} />
              </a>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{opacity: 0, scale: 0.96}}
          animate={{opacity: 1, scale: 1}}
          transition={{duration: 0.6, delay: 0.15}}
          className="flex justify-center md:justify-end">
          <img
            src="/avatar.jpg"
            alt="Portrait of Dinesh Bahadur Khawas"
            className="w-56 h-56 sm:w-64 sm:h-64 rounded-full object-cover border border-line"
          />
        </motion.div>
      </div>

      <TechStack />
    </div>
  );
}
